import yaml from "js-yaml";
import { memoize } from "lodash";
import DOMPurify from "dompurify";

export const JINJA_INCLUDE_REGEX =
  /{%\s*include\s+(?:(\[(?:\s*(?:["'][^"']+["'])\s*(?:,\s*["'][^"']+["']\s*)*)\])|(?:["']([^"']+)["'])|(\w+(?:\.\w+)*))\s*(?:(?:with(?:out)?\s+context|ignore\s+missing)\s*)*\s*%}/g;
export const JINJA_VARIABLE_REGEX =
  /\{\{\s*([^}]+?)\s*\}\}|\{%\sif\s([^}]+?)\s.*?%\}|\{%\selif\s([^}]+?)\s.*?%\}/g;

export const convertToJSON = (yamlString) => {
  try {
    const result = yaml.load(yamlString);
    return { data: result || {}, error: null };
  } catch (error) {
    return {
      data: {},
      error: {
        message: `Error parsing YAML: ${error.message}`,
        details: error,
      },
    };
  }
};

export const createHtml = {
  div: (className, content) => {
    if (!content) return "";
    return `<div class="${className}">${content}</div>`;
  },

  span: (className, content, title = "") => {
    const titleAttr = title ? ` title="${title}"` : "";
    return `<span class="${className}"${titleAttr}>${content}</span>`;
  },

  heading: (level, content) => {
    if (level < 1 || level > 6) {
      throw new Error("Heading level must be between 1 and 6");
    }
    return `<h${level}>${content}</h${level}>`;
  },

  section: (className, content) =>
    `<div class="${className}">\n${content}\n</div>`,

  list: (items, ordered = false) => {
    if (!items || items.length === 0) return "";
    const tag = ordered ? "ol" : "ul";
    const listItems = items.map((item) => `  <li>${item}</li>`).join("\n");
    return `<${tag}>\n${listItems}\n</${tag}>`;
  },

  pre: (className, content) => `<pre class="${className}">${content}</pre>`,
  code: (content) => `<code>${content}</code>`,
  br: () => "<br>",
  p: (content) => `<p>${content}</p>`,
  raw: (html) => DOMPurify.sanitize(html),
};

export const formatHtml = {
  div: createHtml.div,
  span: createHtml.span,
  h1: (content) => createHtml.heading(1, content),
  h2: (content) => createHtml.heading(2, content),
  h3: (content) => createHtml.heading(3, content),
  section: createHtml.section,
  ul: (items) => createHtml.list(items, false),
  pre: createHtml.pre,
  code: createHtml.code,
  br: createHtml.br,
  p: createHtml.p,
  raw: createHtml.raw,
};

export const escapeHtml = (unsafeHtml) => {
  if (typeof unsafeHtml !== "string") return unsafeHtml;
  return DOMPurify.sanitize(unsafeHtml, { USE_PROFILES: { html: true } });
};

export const formatTokenCount = (count) => {
  return count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const calculateTokenCounts = memoize(
  (processedFiles) => {
    const totalTokenCount =
      processedFiles.preprocessing.reduce(
        (sum, file) => sum + file.tokenCount,
        0
      ) +
      processedFiles.primary.reduce((sum, file) => sum + file.tokenCount, 0) +
      processedFiles.postprocessing.reduce(
        (sum, file) => sum + file.tokenCount,
        0
      );

    let adjustedTotal = 0;

    ["preprocessing", "primary", "postprocessing"].forEach((section) => {
      processedFiles[section].forEach((file) => {
        const fileInfo = file.promptInfo;
        if (fileInfo && fileInfo.skipLlm) {
          file.originalTokenCount = file.tokenCount;
          file.tokenCount = 0;
          file.formattedTokenCount = "0";
          file.llmSkipped = true;
        } else {
          adjustedTotal += file.tokenCount;
        }
      });
    });

    return {
      totalTokenCount,
      adjustedTotalTokenCount: adjustedTotal,
      formattedAdjustedTotalTokenCount: formatTokenCount(adjustedTotal),
    };
  },
  (processedFiles) => {
    return JSON.stringify({
      preprocessing: processedFiles.preprocessing.map((f) => ({
        id: f.id,
        tokenCount: f.tokenCount,
        skipLlm: f.promptInfo?.skipLlm,
      })),
      primary: processedFiles.primary.map((f) => ({
        id: f.id,
        tokenCount: f.tokenCount,
        skipLlm: f.promptInfo?.skipLlm,
      })),
      postprocessing: processedFiles.postprocessing.map((f) => ({
        id: f.id,
        tokenCount: f.tokenCount,
        skipLlm: f.promptInfo?.skipLlm,
      })),
    });
  }
);

export const previewLLMCache = {
  sortedPromptNames: new Map(),
  chatCompletionResults: new Map(),
  clearCache: () => {
    previewLLMCache.sortedPromptNames.clear();
    previewLLMCache.chatCompletionResults.clear();
  },
};

export const getFilenameFromPath = (path) => {
  if (!path) return "";
  const pathParts = path.split("/");
  return pathParts[pathParts.length - 1];
};

export const convertRichTextToHtml = (richText) => {
  if (!richText) return "";

  let htmlContent = escapeHtml(richText);

  if (htmlContent.startsWith("{")) {
    try {
      JSON.parse(htmlContent);
      return formatHtml.pre("code-block", formatCodeSyntax(htmlContent));
    } catch (e) {}
  }

  if (htmlContent.includes("```")) {
    return formatCodeBlocks(htmlContent);
  }

  htmlContent = formatLineBreaksAndSpacing(escapeBackticks(htmlContent));

  return formatJinjaTemplates(htmlContent);
};

const escapeBackticks = (content) => {
  if (!content) return "";

  return content
    .replace(/```/g, '<span class="backtick">```</span>')
    .replace(/`/g, '<span class="backtick">`</span>');
};

const formatCodeBlocks = (content) => {
  if (!content) return "";

  let inCodeBlock = false;
  let language = "";
  let result = "";
  let currentBlock = "";

  const lines = content.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const codeBlockMatch = line.match(/^```(\w*)$/);

    if (codeBlockMatch && !inCodeBlock) {
      inCodeBlock = true;
      language = codeBlockMatch[1] || "";
      currentBlock = "";
    } else if (line.trim() === "```" && inCodeBlock) {
      inCodeBlock = false;
      result += formatHtml.pre(
        `code-block ${language}`,
        formatCodeSyntax(currentBlock)
      );
    } else if (inCodeBlock) {
      currentBlock += line + "\n";
    } else {
      result += escapeBackticks(line) + "\n";
    }
  }

  if (inCodeBlock) {
    result +=
      escapeBackticks("```" + language) + "\n" + escapeBackticks(currentBlock);
  }

  return formatLineBreaksAndSpacing(result);
};

const formatCodeSyntax = (code) => {
  if (!code) return "";

  code = preserveSpacing(code);

  code = code
    .replace(/"([^"]+)":/g, '<span class="json-key">"$1":</span>')
    .replace(/: "([^"]+)"/g, ': <span class="json-string">"$1"</span>')
    .replace(/: (true|false)/g, ': <span class="json-boolean">$1</span>')
    .replace(/: (\d+)/g, ': <span class="json-number">$1</span>');

  return code;
};

const formatJinjaTemplates = (content) => {
  if (!content) return "";

  content = content.replace(
    JINJA_VARIABLE_REGEX,
    (match, variable1, variable2, variable3) => {
      return `<span class="jinja-variable">${match}</span>`;
    }
  );

  content = content.replace(JINJA_INCLUDE_REGEX, (match) => {
    return `<span class="jinja-include">${match}</span>`;
  });

  content = content.replace(
    /({%[^%]*%})/g,
    '<span class="jinja-control">$1</span>'
  );

  return content;
};

const preserveSpacing = (content) => {
  if (!content) return "";

  content = content.replace(/\t/g, "    ");

  content = content.replace(/^( +)/gm, (match) => {
    return "&nbsp;".repeat(match.length);
  });

  return content;
};

const formatLineBreaksAndSpacing = (content) => {
  if (!content) return "";

  content = preserveSpacing(content);

  if (
    content.startsWith("{") ||
    content.includes('<span class="backtick">```</span>')
  ) {
    return `<pre style="white-space: pre-wrap;">${content}</pre>`;
  }

  return content.replace(/\n/g, "<br>");
};

export const cleanJsonString = (jsonString) => {
  // Regex pattern explanation:
  // /^```json\s*/ : Matches ```json at the start of the string (^) followed by any whitespace (\s*).
  // (.*?)           : Captures the actual JSON content (non-greedy match for any character, including newlines, if 's' flag is used).
  // \s*```$/        : Matches any whitespace (\s*) followed by ``` at the end of the string ($).

  // The 's' flag (dotall) is crucial for the '.' to match newline characters.
  // The 'i' flag makes it case-insensitive (e.g., matching ```JSON or ```json).
  const pattern = /^```json\s*(.*?)\s*```$/is;

  // Replace the full match with only the captured group (\$1)
  const cleanedString = jsonString.replace(pattern, "$1");

  // Use .trim() just in case of any remaining leading/trailing whitespace
  return cleanedString.trim();
};
